import { adminApiAxios } from '@/api'
import { normalize } from 'normalizr'
import type { DateTime } from 'luxon'

import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

import { transactionSchema } from '../../schema'
import {
  PostTransactionPayload,
  receiveAdminTransactionsByCategory,
  receiveAdminAllTransactions,
  Transaction,
  TransactionResponseEntities,
} from '../../reducers/admin/allTransactions.slice'
import { Dispatch } from '../../utils/typeHelpers'
import { fetchWrapper } from '../../reducers/fetch'
import {
  fetchUserTransactionsByCategoryId,
  TransactionsByCategoryId,
} from '@/features/transactions/transactions.slice'
import { receiveJournalEntries } from '../../reducers/admin/journalEntryReducer'

/*
  Update transaction for user
*/

export const PUT_ADMIN_UPDATE_USER_TRANSACTION_KEY =
  'PUT_ADMIN_UPDATE_USER_TRANSACTION_KEY'

export const adminUpdateUserTransaction = (
  transactionId: number | string,
  data: PostTransactionPayload
) =>
  fetchWrapper({
    defaultErrorMessage: 'Error update transaction',
    defaultValue: false,
    fetchKey: PUT_ADMIN_UPDATE_USER_TRANSACTION_KEY,
    fetchFunction: (dispatch) => {
      return adminApiAxios
        .put<Transaction>(
          `/finances/api/v1/admin/transactions/${transactionId}`,
          data
        )
        .then((json) => {
          const normalizedData = normalize<
            Transaction,
            TransactionResponseEntities,
            string[]
          >(json.data, transactionSchema)
          dispatch(receiveAdminAllTransactions(normalizedData))
          if (normalizedData.entities.journalEntries) {
            dispatch(
              receiveJournalEntries(normalizedData.entities.journalEntries)
            )
          }
          return true
        })
    },
  })

export const ADMIN_USER_TRANSACTIONS_BY_CATEGORY_ID_KEY =
  'ADMIN_USER_TRANSACTIONS_BY_CATEGORY_ID_KEY'

export const fetchAdminUserTransactionsByCategoryId = ({
  id,
  limit,
  startDate,
  endDate,
  userId,
}: {
  id: number | null
  limit: string | null
  startDate: DateTime | undefined
  endDate: DateTime | undefined
  userId: number
}) =>
  fetchWrapper({
    fetchKey: `${ADMIN_USER_TRANSACTIONS_BY_CATEGORY_ID_KEY}${userId}${id}`,
    defaultErrorMessage: 'Error updating transaction.',
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<TransactionsByCategoryId>(
        '/finances/api/v1/admin/transactions/by_category',
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            limit,
            userId,
            transactionCategoryId: id,
          },
        }
      )
      dispatch(
        receiveAdminTransactionsByCategory({
          ...json.data,
          userId,
        })
      )
      return json.data
    },
  })

// Used by the profit and loss reports.  It wraps the admin and user transaction by category calls.  User id sent if is admin
export const fetchTransactionsByCategoryId =
  ({
    id,
    limit,
    startDate,
    endDate,
    userId,
  }: {
    id: number | null
    limit: string | null
    startDate: DateTime | undefined
    endDate: DateTime | undefined
    userId: number | null
  }) =>
  async (dispatch: Dispatch) => {
    return userId
      ? await fetchAdminUserTransactionsByCategoryId({
          id,
          limit,
          startDate,
          endDate,
          userId,
        })(dispatch)
      : await fetchUserTransactionsByCategoryId({
          id,
          limit,
          startDate,
          endDate,
        })(dispatch)
  }

export const FETCH_SINGLE_TRANSACTION_KEY = 'FETCH_SINGLE_TRANSACTION_KEY'
export const fetchSingleTransaction = (id: number | string) =>
  fetchWrapper({
    fetchKey: FETCH_SINGLE_TRANSACTION_KEY,
    defaultErrorMessage: 'Error in retrieving single transaction',
    defaultValue: false as const,
    fetchFunction: async (dispatch: Dispatch) => {
      const json = await adminApiAxios.get<Transaction>(
        `/finances/api/v1/admin/transactions/single/${id}`
      )
      const normalizedData = normalize<
        Transaction,
        TransactionResponseEntities,
        string[]
      >(json.data, transactionSchema)
      dispatch(receiveAdminAllTransactions(normalizedData))
      return json
    },
  })

export const FETCH_ADMIN_IMPORTED_TRANSACTIONS_KEY =
  'FETCH_ADMIN_IMPORTED_TRANSACTIONS_KEY'
export interface AdminImportedTransaction {
  id: number
  date: string
  description: string
  amountInCents: number
  financialAccount: {
    plaidInstitutionName: string
    name: string
    mask: string
  }
  importedOn: string
}
export type AdminImportedTransactionsResponse = Record<
  string,
  AdminImportedTransaction[]
>
export const fetchAdminImportedTransactions = (
  userId: number | string | undefined
) =>
  fetchWrapper({
    fetchKey: FETCH_ADMIN_IMPORTED_TRANSACTIONS_KEY,
    defaultErrorMessage: 'Error fetching imported transactions',
    fetchFunction: async () => {
      if (!userId) {
        return undefined
      }
      const json = await adminApiAxios.get<AdminImportedTransactionsResponse>(
        `/finances/api/v1/admin/transactions/by_imported_on?userId=${userId}`
      )
      return json.data
    },
  })
